import { clsx } from "clsx";
import { clamp as _clamp } from "ramda";
import {
  Children,
  type CSSProperties,
  forwardRef,
  isValidElement,
  type PropsWithChildren,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Key } from "react-aria";
import { FocusRing, useMove } from "react-aria";

import { useDOMRef, useResizeObserver } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import { FixedPanel, type FixedPanelProps } from "./components/FixedPanel.tsx";
import { FixedPanelTab } from "./components/FixedPanelTab.tsx";
import { FlexPanel } from "./components/FlexPanel.tsx";
import { PanelGroupContext, type PanelGroupContextValue } from "./hooks/use-panel-group-context.ts";
import style from "./PanelGroup.module.css";
import { DEFAULT_MIN_SIZE, IN_BETWEEN_SPACE, PanelOrientation, PanelPosition } from "./settings.ts";
import { extractTabsFromChildren } from "./utils/extract-tabs-from-children.ts";

// Types
export interface PanelGroupProps {
  orientation: PanelOrientation;
  fixedPanel: PanelPosition;
  defaultSize?: number | null;
  maxSize?: number;
  minSize?: number;
  isContinuous?: boolean;
  onMoveEnd?: (size: number, max: number | undefined, isClosed: boolean) => void;
  className?: string;
  style?: CSSProperties;
  defaultOpen?: boolean;
  closedSize?: number;
  noResize?: boolean;
  // Tab props (optional)
  defaultTabId?: Key;
  onTabChange?: (tabId: Key) => void;
  tabsActions?: React.ReactNode;
  openOnTabPress?: boolean;
}

type Size = [number | undefined | null, number | undefined | null];
type MoveCb = (size: number, isClosed: boolean) => void;

// Main PanelGroup component
const PanelGroupRoot = forwardRef<HTMLDivElement, PropsWithChildren<PanelGroupProps>>(function PanelGroup(props, ref) {
  const domRef = useDOMRef(ref);
  const rect = useResizeObserver(domRef);
  const moveCbRef = useRef<MoveCb>();
  const moveEndCbRef = useRef<MoveCb>();

  const dataAttributes = useDataAttributes(props);

  // Tab state
  const [activeTabId, setActiveTabId] = useState<Key | null>(props.defaultTabId || null);

  // Call onTabChange when tab changes
  useEffect(() => {
    if (activeTabId && props.onTabChange) {
      props.onTabChange(activeTabId);
    }
  }, [activeTabId, props.onTabChange]);

  const defaultSize = useMemo(() => {
    if (props.defaultSize != null) {
      if (props.closedSize != null) {
        return Math.max(props.defaultSize, props.closedSize);
      }
      return props.defaultSize;
    }
    return null;
  }, [props.defaultSize, props.closedSize]);

  const [HVSize, setHVSize] = useState<Size>([defaultSize, defaultSize]);
  const start = useRef<number | undefined | null>();
  const delta = useRef(0);
  const [isResizing, setResizing] = useState(false);
  const hasBeenManuallyResized = useRef(false);
  const willClose = useRef(false);
  const [isOpen, setIsOpen] = useState(props.defaultOpen ?? true);

  const max = useMemo(() => {
    const HVMax = rect ? [rect.width, rect.height] : undefined;
    const max = HVMax?.[props.orientation === PanelOrientation.Horizontal ? 0 : 1];
    return max != null ? max - IN_BETWEEN_SPACE : 0;
  }, [rect, props.orientation]);

  const maxSize = useMemo(() => {
    if (props.maxSize != null) {
      if (props.closedSize != null) {
        return Math.max(props.closedSize, props.maxSize);
      }
      return props.maxSize;
    }
    return max;
  }, [props.maxSize, props.closedSize, max]);

  const minSize = useMemo(
    () => Math.max(props.minSize ?? DEFAULT_MIN_SIZE, props.closedSize ?? DEFAULT_MIN_SIZE),
    [props.minSize, props.closedSize],
  );

  const clamp = useCallback(
    (pos?: number | null) => {
      if (pos == null) return undefined;
      if (minSize > maxSize) {
        return maxSize;
      }
      return _clamp(minSize, maxSize, pos);
    },
    [minSize, maxSize],
  );

  const setOrientationSize = (size: number) => {
    setHVSize(([h, v]) => {
      const clampedSize = clamp(size)!;
      return props.orientation === PanelOrientation.Horizontal ? [clampedSize, v] : [h, clampedSize];
    });
  };

  const getOrientationSize = (): number | undefined | null => {
    if (!rect) return undefined;
    return clamp(HVSize[props.orientation === PanelOrientation.Horizontal ? 0 : 1]);
  };

  // Check if we have tabs by looking for FixedPanel with FixedPanelTab children
  const childrenArray = Children.toArray(props.children);
  const fixedPanelChild = childrenArray.find((child) => isValidElement(child) && child.type === FixedPanel) as
    | ReactElement<FixedPanelProps>
    | undefined;

  const hasTabs = fixedPanelChild
    ? Children.toArray(fixedPanelChild.props.children).some(
        (child) => isValidElement(child) && child.type === FixedPanelTab,
      )
    : false;

  // Move handlers
  const { moveProps } = useMove({
    onMoveStart: () => {
      setResizing(true);
      if (isOpen) {
        start.current = getOrientationSize();
      } else {
        start.current = props.closedSize ?? 0;
        setIsOpen(props.isContinuous ?? false);
      }
      hasBeenManuallyResized.current = true;
    },
    onMove: (e) => {
      const move = props.orientation === PanelOrientation.Horizontal ? e.deltaX : e.deltaY;
      delta.current += move;

      if (start.current != undefined) {
        if (props.isContinuous) {
          const move =
            props.fixedPanel === PanelPosition.Leading ? start.current + delta.current : start.current - delta.current;

          willClose.current = move < Math.max(minSize / 2, props.closedSize ?? 0);
          setIsOpen(!willClose.current);

          setOrientationSize(clamp(move)!);
          moveCbRef.current?.(clamp(move)!, willClose.current);
        } else {
          let move = delta.current;
          let _current = start.current;
          let _move = delta.current;
          if (props.fixedPanel === PanelPosition.Leading) {
            const _max = maxSize;
            const _min = minSize;
            move = _clamp(_min, _max, _current + delta.current) - _current;
            _move = delta.current + _current;
          } else {
            _current = max - start.current;
            const _max = max - minSize;
            const _min = max - maxSize;
            move = _clamp(_min, _max, _current + delta.current) - _current;
            _move = max - _current - delta.current;
          }

          willClose.current = _move < Math.max(minSize / 2, props.closedSize ?? 0);
          const closePosition = props.fixedPanel === PanelPosition.Leading ? -1 * _current : max - _current;
          domRef.current?.style.setProperty("--handler-move", `${willClose.current ? closePosition : move}px`);
        }
      }
    },
    onMoveEnd: () => {
      if (start.current != null) {
        const move =
          props.fixedPanel === PanelPosition.Leading
            ? clamp(start.current + delta.current)
            : clamp(start.current - delta.current);
        props.onMoveEnd?.(move!, max, willClose.current);
        moveCbRef.current?.(move!, willClose.current);
        moveEndCbRef.current?.(move!, willClose.current);
        if (!props.isContinuous) {
          setOrientationSize(move!);
        }
        delta.current = 0;
        domRef.current?.style.setProperty("--handler-move", "0px");
      }

      if (willClose.current) {
        if (start.current) setOrientationSize(start.current);
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }

      setResizing(false);
    },
  });

  // Effects
  useEffect(() => {
    if (defaultSize == null && !hasBeenManuallyResized.current) {
      const HVMid: Size = rect ? [Math.floor(rect.width / 2), Math.floor(rect.height / 2)] : [undefined, undefined];
      setHVSize(HVMid);
    }
  }, [defaultSize, rect]);

  // Set initial active tab
  useEffect(() => {
    if (hasTabs && fixedPanelChild && !activeTabId) {
      const tabs = extractTabsFromChildren(fixedPanelChild.props.children);
      if (tabs.length > 0) {
        setActiveTabId(tabs[0].id);
      }
    }
  }, [hasTabs, fixedPanelChild, activeTabId]);

  // Context value
  const contextValue: PanelGroupContextValue = {
    orientation: props.orientation,
    fixedPanel: props.fixedPanel,
    isOpen,
    currentSize: getOrientationSize(),
    max,
    maxSize,
    minSize,
    isResizing,
    isContinuous: props.isContinuous ?? false,
    closedSize: props.closedSize ?? 0,
    noResize: props.noResize ?? false,
    activeTabId,
    setActiveTabId,
    tabsActions: props.tabsActions,
    openOnTabPress: props.openOnTabPress,
    openPanel: () => setIsOpen(true),
    closePanel: () => setIsOpen(false),
    togglePanel: () => setIsOpen((isOpen) => !isOpen),
  };

  // Render panels with grip between them
  const renderPanelsWithGrip = () => {
    const fixedPanelChild = childrenArray.find((child) => isValidElement(child) && child.type === FixedPanel);
    const flexPanelChild = childrenArray.find((child) => isValidElement(child) && child.type === FlexPanel);

    if (!fixedPanelChild || !flexPanelChild) {
      return props.children;
    }

    if (props.fixedPanel === PanelPosition.Leading) {
      return (
        <>
          {fixedPanelChild}
          {!props.noResize && (
            <FocusRing focusRingClass={style.focused}>
              <div className={style.grip} {...moveProps} tabIndex={0} />
            </FocusRing>
          )}
          {flexPanelChild}
        </>
      );
    } else {
      return (
        <>
          {flexPanelChild}
          {!props.noResize && (
            <FocusRing focusRingClass={style.focused}>
              <div className={style.grip} {...moveProps} tabIndex={0} />
            </FocusRing>
          )}
          {fixedPanelChild}
        </>
      );
    }
  };

  return (
    <PanelGroupContext.Provider value={contextValue}>
      <div
        className={clsx(style.grid, { [style.dragging]: isResizing }, props.className)}
        style={{ visibility: rect ? "visible" : "hidden", ...props.style }}
        ref={domRef}
        data-testid={dataAttributes["data-testid"] ? `${dataAttributes["data-testid"]}__panel-group` : "panel-group"}
        data-orientation={props.orientation}
      >
        {renderPanelsWithGrip()}
      </div>
    </PanelGroupContext.Provider>
  );
});

// Compound component with sub-components
export const ResizableTabPanels = Object.assign(PanelGroupRoot, {
  FixedPanel,
  FlexPanel,
  FixedPanelTab,
});

// export { FixedPanel, FixedPanelTab, FlexPanel };
